import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';

/**
 * Routes mounted outside `AppFrameLayout` (see `Routes.tsx`) share the webview
 * chrome but are not the embedded "home" stack: they must not participate in
 * `isFirstPage` / native-drawer semantics, only real history back.
 */
function isStandaloneClientWebviewStackPath(pathname: string): boolean {
  const prefixes = ['/purchase', '/finders', '/public/finders'];
  return prefixes.some((prefix) => pathname.startsWith(prefix));
}

interface Props {
  forceClose?: boolean;
}
export const WebviewBackButton: React.FC<Props> = ({ forceClose = false }) => {
  const {
    isWebview,
    postMessage,
    closeable,
    supportsNativeDrawerToggle,
    toggleNativeDrawer,
  } = useNativeBridge();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const isFirstPage = () => {
    if (isStandaloneClientWebviewStackPath(pathname)) {
      return false;
    }

    // Check if it's the default history entry OR a base route
    const baseRoutes = [
      '/clients/',
      '/embed/dashboard/',
      '/appointments/',
      '/messages/',
    ];
    // `preserveFirstPage` is for in-frame redirects that should still behave
    // like the webview root (not used on standalone /purchase/*).
    const state = location.state as { preserveFirstPage?: boolean } | null;
    return (
      location.key === 'default' ||
      !!state?.preserveFirstPage ||
      baseRoutes.includes(pathname)
    );
  };

  const useHamburger = supportsNativeDrawerToggle() && isFirstPage();

  const handleBackClick = () => {
    if (useHamburger) {
      void toggleNativeDrawer();
    } else {
      if (isFirstPage() || forceClose) {
        postMessage('close'); // Close the window
      } else {
        navigate(-1); // Go back in history
      }
    }
  };

  const backIcon = useHamburger ? (
    <Icon icon={faBars} />
  ) : (
    <Icon icon={faChevronLeft} />
  );

  if (!isWebview) return null;
  if (
    isFirstPage() &&
    !closeable &&
    !supportsNativeDrawerToggle()
  ) {
    return null;
  }

  return (
    <IconButton onClick={handleBackClick} sx={{ mr: 0 }}>
      {backIcon}
    </IconButton>
  );
};
